import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import {
  ArtworkDynamicData,
  ChangeGridSizePayload,
  FetchArtworksPayload,
  FetchArtworksState,
  RequestAddFavoritePayload,
  RequestAddWaitlistPayload,
  ResponseAddFavoritePayload,
  ResponseAddWaitlistPayload,
  RequestRemoveWaitlistPayload,
  ResponseRemoveWaitlistPayload,
  SetInitialArtworkPayload,
} from '@definitions/artworks.types';
import { ARTWORKS_LIMIT, FILTER_OPTIONS, STORE_FRONT_KEY } from '@modules/tradeArtworks/tradeArtworks.constants';

import api from '../../services/api';

const PREFIX = 'TRADE_ARTWORKS';

export const changeGridSize = createAction<ChangeGridSizePayload>(`${PREFIX}/CHANGE_GRID_SIZE`);

export const setInitialArtworks = createAction<SetInitialArtworkPayload>(`${PREFIX}/SET_INITIAL_ARTWORKS`);

const fetchTradeArtworksFunc = async ({
  page = 1,
  filterOptions = {},
  search = '',
  infiniteLoader = false,
  offset,
  limit = ARTWORKS_LIMIT,
}: FetchArtworksState) => {
  const { price, inputPrice, ...restFilterOptions } = filterOptions;
  if (filterOptions.inputPrice) {
    if (price) {
      restFilterOptions.price = typeof price === 'string' ? [price, inputPrice] : [...price, inputPrice];
    } else {
      restFilterOptions.price = inputPrice;
    }
  }

  if (price && !inputPrice) {
    restFilterOptions.price = price;
  }

  const query = {
    limit: limit,
    offset: offset || limit * (page - 1),
    storefrontKey: STORE_FRONT_KEY,
    search,
    ...restFilterOptions,
  };

  const { data } = await api.get('/trade/', {
    params: query,
  });

  return {
    artworks: data.results,
    meta: { count: data.count, hasNext: !!data.next, page },
    infiniteLoader,
  };
};

export const fetchTradeArtworks = createAsyncThunk<FetchArtworksPayload, FetchArtworksState>(
  `${PREFIX}/FETCH_TRADE_ARTWORKS`,
  fetchTradeArtworksFunc
);

export const fetchFavoriteTradeArtworks = createAsyncThunk<FetchArtworksPayload, void>(
  `${PREFIX}/FETCH_FAVORITE_ARTWORKS`,
  () => fetchTradeArtworksFunc({ page: 0, filterOptions: { filter: FILTER_OPTIONS.FAVORITES }, limit: 7 })
);

const postWithBCToken = (url: string, payload?: any) =>
  api.post(url, { storefrontKey: STORE_FRONT_KEY, ...(payload && payload) });

const deleteWithBCToken = (url: string, payload?: any) =>
  api.delete(url, {
    data: { storefrontKey: STORE_FRONT_KEY, ...(payload && payload) },
  });

export const addToFavorite = createAsyncThunk<ResponseAddFavoritePayload, RequestAddFavoritePayload>(
  `${PREFIX}/ADD_TO_FAVORITE`,
  async ({ slug }: { slug: string }) => {
    await postWithBCToken(`/artworks/${slug}/favorites/`);

    return { slug };
  }
);

export const removeFromFavorite = createAsyncThunk<ResponseAddFavoritePayload, RequestAddFavoritePayload>(
  `${PREFIX}/REMOVE_FROM_FAVORITE`,
  async ({ slug }: { slug: string }) => {
    await deleteWithBCToken(`/artworks/${slug}/favorites/`);

    return { slug };
  }
);

export const addToWaitlist = createAsyncThunk<ResponseAddWaitlistPayload, RequestAddWaitlistPayload>(
  `${PREFIX}/ADD_TO_WAITLIST`,
  async ({
    email,
    firstName,
    lastName,
    slug,
  }: {
    email: string;
    firstName: string;
    lastName: string;
    slug: string;
  }) => {
    await postWithBCToken(`/artworks/${slug}/waitlist/`, { email, firstName, lastName, slug });

    return { email, firstName, lastName, slug };
  }
);

export const removeFromWaitlist = createAsyncThunk<ResponseRemoveWaitlistPayload, RequestRemoveWaitlistPayload>(
  `${PREFIX}/REMOVE_FROM_WAITLIST`,
  async ({ slug }: { slug: string }) => {
    await deleteWithBCToken(`/artworks/${slug}/waitlist/`, { slug });

    return { slug };
  }
);

export const resetArtworks = createAction(`${PREFIX}/RESET_ARTWORKS`);

export const setArtworkDynamicData = createAction<ArtworkDynamicData>(`${PREFIX}/SET_ARTWORK_DYNAMIC_DATA`);

export const setCurrentPage = createAction<number>(`${PREFIX}/SET_CURRENT_PAGE`);
